/**
 * Reseller Dashboard (`/dashboard`, TMMRSL-0031 / TMMRSL-0058): KPIs for the reseller's one
 * tenant, a per-currency money block (never summed across currencies — `PerCurrency`) in
 * reconciliation order with a note on what the payout figures count (TMMRSL-0047).
 * `GET /dashboard?from=&to=`; the period is the reseller-scoped `PeriodPicker` key. Right under
 * the header, the billing attention banners (TMMRSL-0046) link to `/billing` when a charge, the
 * card or official launch needs action. A not-sellable tenant shows a warning alert.
 */
import { useCallback, useState } from 'react';
import { Alert, Box, Grid, Link, Skeleton, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { notSellableText } from '@/business/notSellable';
import type { TenantPair } from '@/api/generated';
import { resellerAdminApi, type DashboardMoney, type ResellerDashboard } from '@/api/resellerAdmin';
import { ApiErrorState } from '@/components/ApiErrorState';
import { PageHeader } from '@/components/PageHeader';
import { PerCurrency } from '@/components/PerCurrency';
import { PeriodPicker } from '@/components/PeriodPicker';
import { ReportScope } from '@/components/ReportScope';
import { useRemote } from '@/components/useRemote';
import { formatCount, formatSignedCount } from '@/i18n/format';
import type { PeriodRange } from '@/i18n/periods';
import { useLocale } from '@/i18n/useLocale';
import { BillingAttention } from '@/features/resellerBilling/BillingAttention';
import { KpiTile } from './KpiTile';

function shownCount(n: number, locale: string): string {
  return formatCount(n, locale);
}

/** New minus churned. Both counts are always sent. */
function signedNet(added: number, removed: number, locale: string): string {
  return formatSignedCount(added - removed, locale);
}

function NotSellableAlert({ tenant }: { tenant: TenantPair }) {
  const { t } = useTranslation();
  // The pair carries no currency, so a currency reason reads without one.
  const reason = notSellableText(tenant.sellableReason, null, t);
  return (
    <Alert severity="warning">
      {t('resellerDashboard.notSellable')}
      {reason ? ` ${reason}` : null}{' '}
      <Link component={RouterLink} to="/business">
        {t('tenant.tab.overview')}
      </Link>
    </Alert>
  );
}

export function ResellerDashboardScreen() {
  // Null until the picker reports its (restored) period, so the first fetch already has it.
  const [range, setRange] = useState<PeriodRange | null>(null);
  return (
    <Box>
      <PageHeader
        id="dashboard"
        actions={<PeriodPicker screen="dashboard" onChange={setRange} />}
      />
      <Stack spacing={2.5}>
        <BillingAttention showLink />
        {range ? <DashboardBody from={range.from} to={range.to} /> : null}
      </Stack>
    </Box>
  );
}

function DashboardBody({ from, to }: { from: string | undefined; to: string | undefined }) {
  const { t } = useTranslation();
  const load = useCallback(() => resellerAdminApi.getDashboard({ from, to }), [from, to]);
  const { status, data, error, reload } = useRemote(load);

  if (status === 'error') return <ApiErrorState what={t('errors.what.dashboard')} error={error} onRetry={reload} />;
  if (!data) {
    return (
      <Box role="status" aria-label={t('common.loading')}>
        <Skeleton variant="rounded" height={112} />
        <Skeleton variant="rounded" height={190} sx={{ mt: 2 }} />
      </Box>
    );
  }
  return <DashboardReady dashboard={data} />;
}

function DashboardReady({ dashboard }: { dashboard: ResellerDashboard }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const subs = dashboard.subscribers;
  const inPeriod = t('dashboard.kpi.inPeriod');
  const now = t('dashboard.kpi.now');
  const tenant = dashboard.tenant;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <ReportScope from={dashboard.from} to={dashboard.to} />
      {tenant?.sellable === false ? <NotSellableAlert tenant={tenant} /> : null}

      <Grid container spacing={2}>
        <Grid size={{ xs: 6, md: 4, lg: 2.4 }}>
          <KpiTile
            label={t('dashboard.kpi.active', { period: now })}
            value={shownCount(subs.active, locale)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2.4 }}>
          <KpiTile
            label={t('dashboard.kpi.trialing', { period: now })}
            value={shownCount(subs.trialing, locale)}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2.4 }}>
          <KpiTile
            label={t('dashboard.kpi.newChurned', { period: inPeriod })}
            value={`${shownCount(subs.new, locale)} / ${shownCount(subs.churned, locale)}`}
            note={t('dashboard.kpi.net', {
              n: signedNet(subs.new, subs.churned, locale),
            })}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2.4 }}>
          <KpiTile
            label={t('resellerDashboard.kpi.guestsSeats', { period: now })}
            value={t('resellerDashboard.kpi.guestsSeatsValue', {
              guests: shownCount(dashboard.guests, locale),
              seats: shownCount(dashboard.seats, locale),
            })}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 4, lg: 2.4 }}>
          <KpiTile
            label={t('resellerDashboard.kpi.connected', { period: now })}
            value={shownCount(dashboard.connectedAccounts, locale)}
          />
        </Grid>
      </Grid>

      <Box>
        <Typography variant="subtitle1" component="h2">
          {t('resellerDashboard.moneyTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {t('money.perCurrencyNote')}
        </Typography>
        <PerCurrency<DashboardMoney>
          rows={dashboard.money}
          label={t('resellerDashboard.moneyTitle')}
          empty={t('resellerDashboard.moneyEmpty')}
          // The reconciliation order (TMMRSL-0047): price → margin → net → paid out, or charged to
          // the card. Every amount is required (TMMAPI-0164).
          columns={[
            { key: 'retail', label: t('money.retail'), minor: (row) => row.retailMinor },
            { key: 'margin', label: t('money.margin'), minor: (row) => row.resellerMarginMinor },
            {
              key: 'affiliate',
              label: t('money.affiliateCommission'),
              minor: (row) => row.affiliateCommissionMinor,
            },
            {
              key: 'share',
              label: t('money.resellerShare'),
              minor: (row) => row.resellerShareMinor,
            },
            { key: 'paid', label: t('money.payoutsPaid'), minor: (row) => row.payoutsPaidMinor },
            {
              key: 'pending',
              label: t('money.payoutsPending'),
              minor: (row) => row.payoutsPendingMinor,
            },
            {
              key: 'charged',
              label: t('money.chargedToCard'),
              minor: (row) => row.chargedToCardMinor,
            },
          ]}
        />
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('resellerDashboard.payoutsNote')}
        </Typography>
      </Box>
    </Box>
  );
}
